import { Observable, shareReplay } from 'rxjs';
import { assert, UserError } from '@brainfocus/web/utils/assert';
import { isSessionStale, staleReason } from '@brainfocus/web/services/session_identity';

declare global {
    // Variable set in base html by flask
    let FLASK_CSRF: string;
    /**
     * Id of the user this page was rendered for, or null when nobody is signed
     * in. Set in base html by flask; used to refuse requests from a tab whose
     * session now belongs to a different user (#865).
     */
    let BF_SESSION_USER: number | null;
}

/**
 * Path Flask-Login redirects unauthenticated requests to (`login.login_view`
 * in `app/__init__.py`).
 */
const LOGIN_PATH = '/login';

/**
 * Message shown when the browser session no longer authenticates this tab.
 * Deliberately short and actionable: it is rendered verbatim in a banner or an
 * alert, and it must never carry technical detail (#865).
 */
export const SESSION_LOST_MESSAGE = 'Your session is no longer valid. Please refresh the page and sign in again.';

/**
 * The browser session no longer authenticates the tab that issued the call.
 *
 * Two sessions cannot coexist in one browser: the session cookie is
 * browser-wide, so the LAST login wins and every other tab keeps rendering a
 * UI built for a user it can no longer act as. Requests from those tabs hit
 * `@login_required`, which redirects them to the login page (see the detection
 * in `execFetch`). Scoping sessions per tab — or refusing a second, conflicting
 * login in the same browser — is deliberately out of scope here: this hotfix
 * detects the situation and reports it, it does not change the session model.
 *
 * A `UserError`, so every error renderer shows its message verbatim instead of
 * prefixing it with a generic label.
 */
export class SessionExpiredError extends UserError {

    public override name = 'SessionExpiredError';

    constructor() {
        super(SESSION_LOST_MESSAGE);
    }

}

/**
 * Backend error code for "nobody is authenticated for this request" (#871).
 *
 * Answered as `401 {"error": "session_expired"}` by
 * `app/lib/unauthorized.py`, which is where the string is also defined. Matched
 * as a literal on both sides, and both carry a comment saying so — changing it
 * in one place alone silently leaves the user with a bare error code instead of
 * the message below.
 */
export const SESSION_EXPIRED_ERROR = 'session_expired';

/**
 * Header declaring which user the page was rendered for. The backend
 * (`app/lib/session_identity_gate.py`) refuses the request with `409
 * {"error": "session_user_mismatch"}` when it no longer matches the session.
 */
export const SESSION_USER_HEADER = 'X-BF-Session-User';

/** Backend error code for a page/session identity mismatch. */
export const SESSION_MISMATCH_ERROR = 'session_user_mismatch';

/**
 * What happened, when this tab belongs to a different account than the one now
 * signed in. Split from the recovery sentence because the recovery differs by
 * where the message is read: a detached window cannot reload itself, so the
 * stale-session notice pairs this lead with its own tail (#896 review).
 */
export const SESSION_CHANGED_LEAD = 'This tab belongs to a different account than the one currently signed in.';

/**
 * Message shown when this tab belongs to a different account than the one now
 * signed in. Reloading is the recovery: it rebuilds the page for the current
 * user instead of silently acting as them.
 */
export const SESSION_CHANGED_MESSAGE = `${SESSION_CHANGED_LEAD} Reload the page to continue.`;

/**
 * What happened, when the session ended rather than changing hands — a sign-out
 * in another tab.
 *
 * Separate from {@link SESSION_CHANGED_LEAD} because that one names an account
 * that, in this case, does not exist: saying "a different account is signed in"
 * after a sign-out is simply untrue. Found in #871 QA.
 */
export const SESSION_SIGNED_OUT_LEAD = 'You were signed out in another tab.';

/** Message shown after a sign-out in another tab. */
export const SESSION_SIGNED_OUT_MESSAGE = `${SESSION_SIGNED_OUT_LEAD} Reload the page to sign in again.`;

/**
 * The page was rendered for one user and the browser session now belongs to
 * another — someone signed in as a different user in another tab.
 *
 * Without this, the action still executed, attributed to whoever the session
 * belonged to: verified during #865 QA, where a patient delete pressed in a tab
 * rendered for the super admin was carried out under a second user's identity,
 * with nothing shown to the user.
 */
export class SessionChangedError extends UserError {

    public override name = 'SessionChangedError';

    constructor() {
        super(SESSION_CHANGED_MESSAGE);
    }

}

/**
 * Identity of the user this page was rendered for, or `undefined` on a page
 * with no authenticated user (the login page). Injected by
 * `app/templates/layout/body.html`; read defensively because tests and the
 * pre-login pages do not define it.
 */
export function sessionUserHeaders(): Record<string, string> {
    const user = typeof BF_SESSION_USER === 'undefined' ? null : BF_SESSION_USER;
    if (user === null || user === undefined) {
        return {};
    }
    const declared = String(user).trim();
    return declared ? { [SESSION_USER_HEADER]: declared } : {};
}

/**
 * Whether a successful response is in fact NOT the payload the API call asked
 * for, but the login page served after the session stopped authenticating the
 * tab.
 *
 * `fetch` follows redirects transparently, so Flask-Login's `302 -> /login`
 * reaches the client as a `200 text/html` and used to be consumed as a valid
 * response — the document then ended up rendered in an error banner, CSRF
 * token included (#865).
 *
 * Since #871 this is the FALLBACK, not the primary signal: the backend states
 * session loss as `401 {"error": "session_expired"}` for requests it can tell
 * came from this layer. It is kept because the redirect still happens for
 * everything that cannot read JSON — a page rendered before #871 was deployed, a
 * file download, and the SSE progress stream (`EventSource` sends no headers).
 *
 * The login page is only ever reached by FOLLOWING that redirect — no API
 * endpoint redirects on success — so a response that was not redirected is
 * never the login page, whatever its content type. The content type alone
 * used to be the signal, and a bare Flask `"", 204` (stamped `text/html;
 * charset=utf-8` by default) was read as a lost session right after the write
 * had been committed (#949). It now counts only together with `redirected`:
 * every BrainFocus endpoint answers `application/json` or
 * `application/octet-stream`, so redirected HTML means "not the API answer"
 * even when the final URL cannot be read. `headers`, `redirected` and `url`
 * are all read defensively: hand-rolled `Response` objects (widely used
 * across the test suites) may omit them, and so may a browser on an opaque
 * response.
 */
function isSessionLossResponse(response: Response): boolean {
    if (response.redirected !== true) {
        return false;
    }

    const contentType = response.headers?.get('content-type') ?? '';
    if (contentType.split(';')[0]?.trim() === 'text/html') {
        return true;
    }

    if (typeof response.url !== 'string' || !response.url) {
        return false;
    }
    try {
        return new URL(response.url, window.location.origin).pathname === LOGIN_PATH;
    } catch {
        return false;
    }
}

/**
 * Full-page navigation indirection. Kept behind an object so the ToS gate
 * redirect (#756) is unit-testable (stub `navigation.assign`) without
 * triggering jsdom's "Not implemented: navigation" during tests.
 */
export const navigation = {
    assign(url: string): void {
        window.location.assign(url);
    },
};

/**
 * Create POST JSON endpoint client function which accepts T and responds with
 * R. Use to quickly prepare functions representing JSON backend endpoints.
 */
export function endpointJson<T extends object | void, R>(url: string): (data: T) => Promise<R> {
    return async (data: T): Promise<R> => {
        const response = await execFetch('POST', url, data ?? {});
        const responseType = response.headers.get('content-type');
        assert(responseType === 'application/json', 'Invalid response');
        return response.json();
    };
}

/**
 * Body returned verbatim by the backend's CSRFError handler
 * (`app/errors.py:55`). Matched as a literal string — the coupling is the point
 * of the constant, and both sides carry a comment saying so.
 */
export const CSRF_REJECTION_ERROR = 'CSRF token missing or invalid';

/**
 * True when a response is the backend refusing a write for a missing or
 * expired CSRF token, and therefore worth retrying with a fresh one (#877).
 * Deliberately narrow: any other 400 is a real error the caller must see.
 */
export function isCsrfRejection(status: number, json: any): boolean {
    return status === 400 && !!json && json.error === CSRF_REJECTION_ERROR;
}

/**
 * In-flight token refresh, shared by every caller for its duration so a burst
 * of rejected writes triggers one request instead of one per write. Cleared
 * once settled, so a later expiry refreshes again.
 */
let pendingCsrfRefresh: Promise<string> | null = null;

/**
 * Fetch a fresh CSRF token and publish it as the token every write path reads.
 *
 * `FLASK_CSRF` is a global assigned by an inline `<script>` in the layout
 * template, so the new value is published on the global object and the existing
 * read sites pick it up on their next read with no change.
 *
 * Written through `globalThis`, not `window`: in the browser they are the same
 * object, but under jsdom the test globals live on Node's `global` while
 * `window` is a separate object, so assigning to `window` would leave the bare
 * `FLASK_CSRF` reads pointing at the stale value.
 */
export function refreshCsrfToken(): Promise<string> {
    if (pendingCsrfRefresh) {
        return pendingCsrfRefresh;
    }
    pendingCsrfRefresh = (async () => {
        let response: Response;
        try {
            // Routed through execFetch so the refresh inherits the ToS and NDA
            // gate recovery. A GET is never CSRF-checked, so this cannot
            // recurse into the retry below.
            response = await execFetch('GET', '/api/csrf-token');
        } catch (e) {
            // The session is gone, not just the token. Reuse the shared
            // SessionExpiredError from #865 so the user gets its short,
            // actionable message rather than the CSRF string. `execFetch` also
            // raises it on its own when the login page is served as a
            // "successful" response, which propagates through the rethrow below.
            if (e instanceof ApiError && e.code === 401) {
                throw new SessionExpiredError();
            }
            throw e;
        }
        const json = await response.json();
        const token: unknown = json?.csrf_token;
        if (typeof token !== 'string' || !token) {
            throw new Error('API Error: CSRF token refresh returned no token');
        }
        (globalThis as unknown as { FLASK_CSRF: string }).FLASK_CSRF = token;
        return token;
    })();
    return pendingCsrfRefresh.finally(() => {
        pendingCsrfRefresh = null;
    });
}

/** Drop the memoised refresh. Test seam only — never call from app code. */
export function resetCsrfRefreshForTests(): void {
    pendingCsrfRefresh = null;
}

/**
 * Options of a request made through this module (FE Arch 1.0, #1165).
 */
export interface RequestOptions {
    /**
     * Hand back the `Response` instead of throwing for any failure the shared
     * gates below do not handle (CSRF, ToS, NDA, expired session, identity
     * mismatch), so callers that build their own message from the body keep it.
     * A successful response is returned undecoded too.
     */
    raw?: boolean;
    /**
     * Let the request outlive the page (`fetch` keepalive), for writes sent
     * while it is being left (#374). `sendBeacon` cannot carry the CSRF header.
     */
    keepalive?: boolean;
    /** Abort after this many milliseconds and reject with `ApiTimeoutError`. No timeout by default. */
    timeout?: number;
}

/** The error a stale window refuses to send with, matching why it is stale (#871). */
export function staleSessionError(): SessionExpiredError | SessionChangedError {
    return staleReason() === 'signed-out' ? new SessionExpiredError() : new SessionChangedError();
}

/**
 * The one place a `fetch` is issued. A request aborted by its `timeout` option
 * rejects with `ApiTimeoutError`, like a gateway timeout.
 */
async function fetchOnce(url: string, init: RequestInit): Promise<Response> {
    try {
        return await fetch(url, init);
    } catch (e) {
        if (init.signal?.aborted) {
            throw new ApiTimeoutError();
        }
        throw e;
    }
}

/**
 * Body of an error response, read from a clone so a raw caller can still read
 * it. Hand-rolled `Response` stand-ins (common in the test suites) may lack
 * `clone`; they are read directly.
 */
async function readErrorJson(response: Response): Promise<any> {
    try {
        return await (typeof response.clone === 'function' ? response.clone() : response).json();
    } catch {
        return undefined;
    }
}

/**
 * Failures every transport recovers from or reports the same way, whatever the
 * caller wants to do with the rest (#1165). Returns the error to raise, or
 * `null` when the failure is not one of these. Navigating gates (ToS, NDA)
 * start the navigation before returning.
 */
function gateFailure(status: number, json: any, method: string, url: string): Error | null {
    // ToS / Privacy login gate (#555): the backend answers blocked API
    // requests with 403 {"error": "tos_acceptance_required"} and delegates
    // the redirect to the client. Drive it here — the single choke point
    // for every API call — so a blocked user is always routed back to the
    // re-acceptance page instead of getting a cascade of red banners with
    // no way out (#756). Full-page navigation tears down the broken view
    // and lets the gate re-evaluate the load as HTML.
    if (status === 403 && json && json.error === 'tos_acceptance_required') {
        if (window.location.pathname !== '/accept-terms') {
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            navigation.assign(`/accept-terms?next=${next}`);
        }
        // Reject the chain so no caller proceeds on a missing body; the page
        // is navigating away, so this rejection is never surfaced.
        return new ApiError(json.error, status);
    }

    // NDA gate (#529): the backend answers blocked API requests for a
    // pending industry-player account with 403 {"error": "nda_pending"}.
    // Drive the recovery here (the single choke point for every API call)
    // so a pending user is routed to the informational page instead of a
    // cascade of red banners (#795 R4). /nda-pending is a static page, so
    // no `next` is needed. Full-page navigation lets the gate re-evaluate.
    if (status === 403 && json && json.error === 'nda_pending') {
        if (window.location.pathname !== '/nda-pending') {
            navigation.assign('/nda-pending');
        }
        return new ApiError(json.error, status);
    }

    // Session gone, stated rather than inferred (#871). Mapped before the
    // generic `json.error` throw below so the user gets the short, actionable
    // sentence instead of the raw code. A 401 raised for any OTHER reason —
    // `admin_required` aborts with 401 for a missing role — deliberately falls
    // through to the generic handling: telling a signed-in user to sign in
    // again would be the wrong instruction.
    if (status === 401 && json && json.error === SESSION_EXPIRED_ERROR) {
        console.error(`Session expired on ${method} ${url}: the backend reports no authenticated user`);
        return new SessionExpiredError();
    }

    // Identity mismatch (#865): this page was rendered for another user and
    // the session now belongs to someone else. The backend refused the
    // request before it did anything; tell the user to reload rather than
    // leaving them to act as an account they did not choose.
    if (status === 409 && json && json.error === SESSION_MISMATCH_ERROR) {
        console.error(`Session identity mismatch on ${method} ${url}: the page was rendered for a different user than the session authenticates`);
        return new SessionChangedError();
    }

    return null;
}

/** Any other failed `fetch` response. */
function genericFailure(status: number, json: any): Error {
    if (json && json.error) {
        const apiError = new ApiError(json.error, status);
        // Surface optional structured fields (e.g. quota limit on a 409) so
        // callers can build user-facing messages.
        if (typeof json.limit === 'number') {
            apiError.limit = json.limit;
        }
        return apiError;
    }

    if (status === 504) {
        return new ApiTimeoutError();
    }

    return new Error(`API Error: ${status}`);
}

export async function execFetch(
    method: string,
    url: string,
    data?: FormData | Record<string, any>,
    options: RequestOptions = {},
): Promise<Response> {
    let body: BodyInit | null;
    let contentType: string | undefined;

    if (typeof data === 'string') {
        body = data;
        contentType = 'plain/text';
    } else if (data instanceof FormData) {
        body = data;
    } else {
        body = JSON.stringify(data);
        contentType = 'application/json';
    }

    // Issuing the request is a closure so the CSRF retry below can replay it
    // verbatim (#877). The token is read per attempt, not captured, so the
    // replay carries the refreshed value.
    //
    // The replay re-sends `body` as built above. FormData and serialised JSON
    // can both be sent twice; a `ReadableStream` body could not, since it
    // cannot be read again. There is no such caller today — if one is added,
    // the retry must be skipped for it.
    const send = (): Promise<Response> => {
        // Always send the CSRF token (required for any non-GET request). For
        // FormData bodies we must NOT set Content-Type ourselves — the browser
        // adds it with the correct multipart boundary — but the previous code
        // dropped ALL headers in that branch, so FormData uploads (e.g. `.trc`
        // videos) were rejected with "CSRF token missing or invalid" (#565).
        // Declare which user this page was rendered for, so the backend can
        // refuse the request if the session now belongs to someone else (#865).
        const headers: Record<string, string> = { 'X-CSRFToken': FLASK_CSRF, ...sessionUserHeaders() };
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        const init: RequestInit = { method, body, headers };
        if (options.keepalive) {
            init.keepalive = true;
        }
        if (options.timeout !== undefined) {
            init.signal = AbortSignal.timeout(options.timeout);
        }
        return fetchOnce(url, init);
    };

    // The window already knows its identity is gone (#871, PR #896 review). The
    // request would be refused by the identity gate anyway, and the application
    // keeps refreshing itself — `manage.tsx` refetches on `visibilitychange` —
    // so letting these leave produced a refusal, and a banner, per focus change.
    // Refuse here instead: no traffic, and the caller gets the error whose
    // message matches the reason, in case anything reports it another way.
    if (isSessionStale()) {
        throw staleSessionError();
    }

    let response = await send();

    // A "successful" login page means the session behind this tab is gone. Bail
    // out here — the single choke point for every API call — so no caller ever
    // receives the document as a payload (#865). Technical detail goes to the
    // console only; the user gets the short message the error carries.
    if (response.status <= 299 && isSessionLossResponse(response)) {
        console.error(`Session lost on ${method} ${url}: HTTP ${response.status} served the login page instead of the expected payload`);
        throw new SessionExpiredError();
    }

    if (response.status > 299) {
        let json = await readErrorJson(response);

        // Expired CSRF token (#877). The token is stamped into the page once,
        // so after WTF_CSRF_TIME_LIMIT every write from a still-open tab is
        // refused until a reload — silently losing work on the paths that do
        // not report the failure. Mint a fresh token and replay ONCE: the
        // rejection happens before the view runs, so nothing was committed and
        // the replay cannot duplicate a write.
        if (isCsrfRejection(response.status, json)) {
            let refreshed = false;
            try {
                await refreshCsrfToken();
                refreshed = true;
            } catch (e) {
                // The session is gone: no retry can help, and the user needs a
                // different message from "CSRF token missing or invalid".
                if (e instanceof SessionExpiredError) {
                    throw e;
                }
                // Any other refresh failure (network, 500) leaves the original
                // rejection as the truth to report.
            }
            if (refreshed) {
                response = await send();
                if (response.status <= 299) {
                    return response;
                }
                json = await readErrorJson(response);
            }
        }

        const gated = gateFailure(response.status, json, method, url);
        if (gated) {
            throw gated;
        }
        if (options.raw) {
            return response;
        }
        throw genericFailure(response.status, json);
    }

    return response;
}

/**
 * Send a request through the shared pipeline and decode the answer by content
 * type: JSON, `ArrayBuffer` for binary, text otherwise. With `raw` the
 * `Response` itself is returned (FE Arch 1.0, #1165).
 */
export async function request(
    method: string,
    url: string,
    data: FormData | Record<string, any> | undefined,
    options: RequestOptions & { raw: true },
): Promise<Response>;
export async function request(
    method: string,
    url: string,
    data?: FormData | Record<string, any>,
    options?: RequestOptions,
): Promise<object | ArrayBuffer | string>;
export async function request(
    method: string,
    url: string,
    data?: FormData | Record<string, any>,
    options: RequestOptions = {},
): Promise<object | ArrayBuffer | string | Response> {
    const response = await execFetch(method, url, data, options);
    if (options.raw) {
        return response;
    }

    const responseType = response.headers.get('content-type');
    if (responseType === 'application/json') {
        return response.json();
    } else if (responseType === 'application/octet-stream') {
        return response.arrayBuffer();
    } else {
        return response.text();
    }
}

// Total request timeout for an upload. Generous enough for a large (~1 GB)
// neuroimage on a slow link, but bounded so a stalled connection rejects with
// a clear error instead of leaving the UI stuck forever (#717).
const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Send POST request using XHR and return observable which emits the upload
 * progress, then the JSON result.
 *
 * XHR because `fetch` cannot report upload progress. It shares the stale
 * session refusal and the failure gates of `execFetch` (#1165), and carries its
 * own expired-token recovery: a rejection for a stale token refreshes the token
 * and replays the upload once (#877). Progress restarts from 0 on the replay,
 * which is honest — the first attempt transferred nothing the server kept.
 */
export function requestWithProgress<T>(url: string, files: Record<string, File | File[] | string>): Observable<ResponseProgress<T>> {
    return new Observable<ResponseProgress<T>>((sub) => {
        if (isSessionStale()) {
            sub.error(staleSessionError());
            return undefined;
        }

        const data = new FormData();
        for (const [key, value] of Object.entries(files)) {
            // Arrays are appended as repeated parts under the same field name
            // (#451), so the backend's request.files.getlist(<key>) receives
            // every file. Single File / string values are appended unchanged.
            if (Array.isArray(value)) {
                for (const file of value) data.append(key, file);
            } else {
                data.append(key, value);
            }
        }

        // The XHR currently in flight, so teardown aborts the right one across
        // a replay. Reassigned by `attempt`.
        let xhr: XMLHttpRequest;
        let cancelled = false;

        /** True when the finished request was refused for a stale CSRF token. */
        const isCsrfRefusal = (request: XMLHttpRequest): boolean => {
            if (request.status !== 400) return false;
            try {
                return JSON.parse(request.responseText)?.error === CSRF_REJECTION_ERROR;
            } catch {
                return false;
            }
        };

        const attempt = (isReplay: boolean): void => {
            xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    sub.next({ progress: e.loaded / e.total });
                }
            });
            xhr.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    sub.next({ progress: e.loaded / e.total });
                }
            });
            xhr.addEventListener('loadend', () => {
                const request = xhr;
                if (request.readyState === 4) {
                    // Expired page token: refresh and replay once. Never twice —
                    // a second refusal is a real failure, not a stale token
                    // (#877). Checked before the handling below, so a stale
                    // token is recovered instead of being reported.
                    if (!isReplay && isCsrfRefusal(request)) {
                        refreshCsrfToken().then(
                            () => {
                                if (!cancelled) attempt(true);
                            },
                            (e: unknown) => {
                                if (e instanceof SessionExpiredError) {
                                    sub.error(e);
                                } else {
                                    // Refresh failed for another reason — report
                                    // the original refusal.
                                    sub.error(new Error(`API Error: ${CSRF_REJECTION_ERROR}`));
                                }
                            },
                        );
                        return;
                    }

                    let json: T | undefined;
                    try {
                        json = JSON.parse(request.responseText);
                    } catch {
                        // NEVER interpolate `responseText`: an upload answered with
                        // the login page used to put that whole document — CSRF
                        // token included — in front of the user, the same leak as
                        // #865. A non-JSON answer to an upload means the session is
                        // gone, so say that instead.
                        sub.error(new SessionExpiredError());
                    }

                    if (request.status !== 200) {
                        let errJson: { error: string } | undefined;
                        try {
                            errJson = JSON.parse(request.responseText);
                        // eslint-disable-next-line no-empty
                        } catch { }
                        // Same gates as every fetch (#1165): ToS / NDA recovery,
                        // expired session, and an upload from a tab whose account
                        // changed (#865), which used to surface the backend's own
                        // code: "API Error: Status 409 Error: session_user_mismatch".
                        const gated = gateFailure(request.status, errJson, 'POST', url);
                        if (gated) {
                            sub.error(gated);
                            return;
                        }
                        sub.error(errJson ? new ApiError(errJson.error, request.status) : new Error(`API Error: Status ${request.status}`));
                    } else if (!json) {
                        sub.error(new Error(`API Error: Empty response`));
                    } else {
                        sub.next({ result: json });
                        sub.complete();
                    }
                } else {
                    sub.error(new Error(`API Error: Unknown error`));
                }
            });
            // A stalled/dropped connection must reject with an actionable message
            // rather than hang: without these handlers a stall never fires loadend
            // and the observable never emits, freezing the upload UI (#717).
            xhr.addEventListener('timeout', () => sub.error(new Error('API Error: Upload timed out — the connection stalled. Please try again.')));
            xhr.addEventListener('error', () => sub.error(new Error('API Error: Network error during upload. Please try again.')));
            xhr.addEventListener('abort', () => sub.error(new Error('API Error: Upload aborted')));
            xhr.open('POST', url, true);
            xhr.timeout = UPLOAD_TIMEOUT_MS;
            // Read per attempt, so the replay carries the refreshed token.
            xhr.setRequestHeader('X-CSRFToken', FLASK_CSRF);
            xhr.setRequestHeader('X-BrainFocus-API', 'yes');
            // Declare the page's user so an upload from a tab whose session now
            // belongs to somebody else is refused rather than attributed to them
            // (#865). Read defensively: absent on pre-login pages and in tests.
            if (typeof BF_SESSION_USER !== 'undefined' && BF_SESSION_USER !== null) {
                xhr.setRequestHeader(SESSION_USER_HEADER, String(BF_SESSION_USER));
            }
            xhr.send(data);
        };

        attempt(false);

        // Teardown: unsubscribing (e.g. the user cancels) aborts the in-flight
        // request instead of letting it run to completion in the background, and
        // stops a pending refresh from starting a replay.
        return () => {
            cancelled = true;
            if (xhr.readyState !== XMLHttpRequest.DONE) {
                xhr.abort();
            }
        };
    }).pipe(shareReplay());
}

export interface ResponseProgress<T> {
    progress?: number;
    result?: T;
}

export type ObservableProgress<T> = Observable<ResponseProgress<T>>;

export class ApiError extends Error {

    /**
     * Optional quota limit attached to structured errors (e.g. the 409
     * `ephemeral_quota_reached` response carries the per-user limit).
     */
    public limit?: number;

    constructor(public readonly apiError: string, public readonly code: number) {
        super(`API Error: ${apiError}`);
    }

}

export class ApiTimeoutError extends ApiError {

    constructor() {
        super(`Request timeout`, 504);
    }

}
